import React from "react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { ErrorNote } from "../../../common/u1/ErrorNote";
import { ArrowRight, CheckIcon, Dirham } from "../../../../assets/ui/icons";
import { BillingCycleToggle } from "./BillingCycleToggle";
import { planLabel, priceNote, priceOf, priceSuffix } from "./membershipFields";

/* The price card, and then the checkout. */
export const MembershipPlanCard = ({ checkout, stripePromise, className = "" }) => {
  const { billing, setBilling, stage, clientSecret, submitting, error } = checkout;
  const note = priceNote(billing);

  const skipButton = (
    <button
      type="button"
      onClick={checkout.skip}
      disabled={submitting}
      className={cn(
        "mt-2.5 w-full rounded p-3 text-[13px] font-medium font-sans",
        "text-[var(--muted)] underline underline-offset-[3px] transition-colors",
        "hover:text-[var(--ink-2)] focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-[var(--orange)]",
        submitting && "cursor-not-allowed opacity-45"
      )}
    >
      Skip for now — stay on Claimed plan
    </button>
  );

  return (
    <div
      className={cn(
        "rounded-[14px] border border-[var(--line-2)] bg-white p-5 sm:p-6",
        className
      )}
    >
      {stage === "pick" ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-[11px] font-bold uppercase tracking-[.11em] text-[var(--muted)] font-sans">
              Membership plan
            </p>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full bg-[var(--success-soft)]",
                "px-2.5 py-1 text-[11px] font-semibold text-[var(--success)] font-sans"
              )}
            >
              <CheckIcon size={11} />
              Most businesses upgrade
            </span>
          </div>

          <BillingCycleToggle
            className="mt-3.5"
            value={billing}
            onChange={setBilling}
          />

          <p className="m-0 mt-4 flex flex-wrap items-baseline gap-2">
            <span className="inline-flex items-center gap-2 text-[38px] font-bold leading-none tracking-[-.03em] text-[var(--ink)] font-sans">
              <Dirham size={28} />
              {priceOf(billing).toLocaleString()}
            </span>
            <span className="text-[13px] text-[var(--muted)] font-sans">
              {priceSuffix(billing)}
            </span>
          </p>

          <p className="m-0 mt-2 inline-flex flex-wrap items-center gap-1 text-[12.5px] text-[var(--muted)] font-sans">
            {note.text || (
              <>
                Equivalent to <Dirham size={12} /> {note.monthlyEquivalent} / month,
                billed annually
              </>
            )}
          </p>

          <p className="m-0 mt-3.5 text-[12.5px] leading-[1.5] text-[var(--muted)] font-sans">
            Card details and any applicable VAT are collected on the next step
            inside our secure checkout.
          </p>

          {error && <ErrorNote className="mt-3.5">{error}</ErrorNote>}

          <Button
            variant="coralSolid"
            className="mt-4 h-12 w-full gap-2 text-[15px]"
            disabled={submitting}
            onClick={checkout.pay}
          >
            {submitting ? (
              "Starting checkout…"
            ) : (
              <>
                Pay &amp; Become a Member <ArrowRight size={15} />
              </>
            )}
          </Button>

          {skipButton}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-[11px] font-bold uppercase tracking-[.11em] text-[var(--muted)] font-sans">
              Paying — {planLabel(billing)} plan
            </p>
            <button
              type="button"
              onClick={checkout.changePlan}
              disabled={submitting}
              className={cn(
                "rounded text-[12px] font-semibold text-[var(--coral)] font-sans",
                "underline underline-offset-[3px] transition-colors",
                "hover:text-[var(--coral-deep)] focus-visible:outline-none",
                "focus-visible:ring-2 focus-visible:ring-[var(--orange)]",
                submitting && "cursor-not-allowed opacity-45"
              )}
            >
              Change plan
            </button>
          </div>

          {error && <ErrorNote className="mt-3.5">{error}</ErrorNote>}

          {/* Stripe's iframe. Its own styling is Stripe's; the only thing this
              reserves is the height, so the card does not jump as it loads. */}
          <div id="checkout" className="mt-3.5 min-h-[540px]">
            {clientSecret && (
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            )}
          </div>

          {skipButton}
        </>
      )}
    </div>
  );
};

export default MembershipPlanCard;
